/**
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *  KARTA „LIVE" — JEDEN CYKL WĘZŁA TO JEDEN WIERSZ.
 *
 *  ⚠️⚠️ ZGŁOSZONE 11.09.2026: „te logi live wydają się zdublowane i są dość nieczytelne".
 *  Nic się nie dublowało — portal pisze OSOBNY wpis dla każdej tabeli konfiguracji i osobny dla
 *  wydań, a agent dosyła wydania przeplotem co 10 s przy cyklu co 60 s. Jeden cykl jednej maszyny
 *  dawał więc trzy–cztery wiersze z tą samą nazwą węzła i tą samą jedynką, a przy dwóch maszynach
 *  ekran robił się nie do przeczytania.
 *
 *  ⚠️⚠️ ŁĄCZYMY PO WĘŹLE I PO CZASIE, ALE TYLKO SĄSIADUJĄCE WPISY. Grupa kończy się, gdy zmienia
 *  się węzeł albo gdy przerwa przekracza jeden cykl — dzięki temu nigdy nie zlepimy dwóch maszyn
 *  ani dwóch odległych zdarzeń w jedno zdanie. To jest ta sama zasada, co przy odsiewie w górę:
 *  wolno streścić, nie wolno zmyślić.
 *
 *  ⚠️⚠️ SUMUJEMY LICZBY Z KONTEKSTU, A NIE SKLEJAMY ZDAŃ. Wpis niesie `{ wydan }` albo
 *  `{ tabela, dodane, zmienione, usuniete, zachowane }`, więc „1 wydanie" + „1 wydanie" daje
 *  „2 wydania", a nie „1 wydanie · 1 wydanie". Sklejanie gotowych zdań wyglądałoby dokładnie jak
 *  to zdublowanie, na które zgłoszenie wskazuje.
 *
 *  ⚠️ WPIS, KTÓREGO NIE UMIEMY ROZEBRAĆ, POKAZUJEMY DOSŁOWNIE. Dziennik jest wspólny i starsze
 *  wpisy mogą nie mieć kontekstu — wtedy lepsze jest surowe zdanie z serwera niż milczenie.
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 */

/** Jeden wpis dziennika, tak jak go oddaje API — bez pól, których ta karta nie czyta. */
export type WpisLive = {
  seq: string | number;
  at: string;
  message: string;
  context?: unknown;
};

export type GrupaLive = {
  /** Klucz listy — `seq` najnowszego wpisu grupy. */
  seq: string;
  /** Czas NAJNOWSZEGO wpisu: grupa obejmuje najwyżej jeden cykl, więc starszy nic nie doda. */
  at: string;
  wezel: string;
  /** Ile wpisów zbiło się w ten wiersz — `1` znaczy „nic nie łączyliśmy". */
  wpisow: number;
  /** Gotowe człony do wyświetlenia: wydania i tabele konfiguracji. */
  czesci: string[];
};

/**
 * GRANICA GRUPY — jeden cykl agenta.
 *
 * ⚠️ 90 s, NIE 60 s: cykl to 60 s, ale wpisy powstają w różnych momentach obsługi paczki
 * (najpierw wydania przeplotem, potem konfiguracja), a portal bywa obciążony. Ciaśniejsze okno
 * rozbijałoby jeden cykl na dwa wiersze dokładnie wtedy, gdy dzieje się najwięcej.
 */
export const OKNO_GRUPY_MS = 90_000;

type Kontekst = { nodeId?: unknown; wydan?: unknown; tabela?: unknown; dodane?: unknown; zmienione?: unknown; usuniete?: unknown; zachowane?: unknown };

const liczba = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/** `nodeId` z kontekstu — bez niego wpis nie ma z czym się grupować. */
export function wezelWpisu(w: WpisLive): string | null {
  const ctx = (w.context ?? {}) as Kontekst;
  return typeof ctx.nodeId === 'string' && ctx.nodeId !== '' ? ctx.nodeId : null;
}

export type Zebrane = {
  wydan: number;
  tabele: Map<string, { dodane: number; zmienione: number; usuniete: number; zachowane: number }>;
  /** Wpisy, których nie umiemy rozebrać — pokazywane dosłownie. */
  surowe: string[];
};

function dodajDoZebranych(z: Zebrane, w: WpisLive): void {
  const ctx = (w.context ?? {}) as Kontekst;
  if (typeof ctx.wydan === 'number') {
    z.wydan += liczba(ctx.wydan);
    return;
  }
  if (typeof ctx.tabela === 'string' && ctx.tabela !== '') {
    const t = z.tabele.get(ctx.tabela) ?? { dodane: 0, zmienione: 0, usuniete: 0, zachowane: 0 };
    t.dodane += liczba(ctx.dodane);
    t.zmienione += liczba(ctx.zmienione);
    t.usuniete += liczba(ctx.usuniete);
    t.zachowane += liczba(ctx.zachowane);
    z.tabele.set(ctx.tabela, t);
    return;
  }
  /** ⚠️ Nie rozumiemy tego wpisu — zdanie z serwera idzie dosłownie, zamiast wypaść z ekranu. */
  z.surowe.push(w.message);
}

/**
 * CZŁONY DO WYŚWIETLENIA.
 *
 * `slowoWydan` i `slowoZachowane` przychodzą z i18n, bo odmiana jest językowa, a ta funkcja
 * ma zostać czysta i sprawdzalna bez tłumaczeń.
 */
export function czesciGrupy(
  z: Zebrane,
  slowa: { wydania: (n: number) => string; zachowane: (n: number) => string },
): string[] {
  const out: string[] = [];
  if (z.wydan > 0) out.push(slowa.wydania(z.wydan));
  for (const [tabela, t] of [...z.tabele.entries()].sort((a, b) => a[0].localeCompare(b[0]))) {
    const liczby: string[] = [];
    if (t.dodane > 0) liczby.push(`+${t.dodane}`);
    if (t.zmienione > 0) liczby.push(`~${t.zmienione}`);
    if (t.usuniete > 0) liczby.push(`−${t.usuniete}`);
    if (t.zachowane > 0) liczby.push(slowa.zachowane(t.zachowane));
    out.push(liczby.length === 0 ? tabela : `${tabela} ${liczby.join(' ')}`);
  }
  out.push(...z.surowe);
  return out;
}

/**
 * ZBICIE LISTY WPISÓW W WIERSZE KARTY.
 *
 * ⚠️ WEJŚCIE JEST POSORTOWANE MALEJĄCO PO CZASIE (tak oddaje je API) i tej kolejności nie ruszamy:
 * grupujemy WYŁĄCZNIE sąsiadów. Sortowanie po swojemu zmieniłoby to, co człowiek widzi na górze.
 */
export function zgrupujLive(
  wpisy: readonly WpisLive[],
  slowa: { wydania: (n: number) => string; zachowane: (n: number) => string },
  oknoMs: number = OKNO_GRUPY_MS,
): GrupaLive[] {
  type Budowana = { seq: string; at: string; wezel: string; wpisow: number; ostatni: number; zebrane: Zebrane };
  const gotowe: Budowana[] = [];

  for (const w of wpisy) {
    const wezel = wezelWpisu(w) ?? '—';
    const czas = new Date(w.at).getTime();
    let grupa = gotowe[gotowe.length - 1];

    if (grupa === undefined || grupa.wezel !== wezel || !Number.isFinite(czas) || grupa.ostatni - czas > oknoMs) {
      grupa = { seq: String(w.seq), at: w.at, wezel, wpisow: 0, ostatni: czas, zebrane: { wydan: 0, tabele: new Map(), surowe: [] } };
      gotowe.push(grupa);
    }

    grupa.wpisow += 1;
    /** ⚠️ Najstarszy czas grupy — od niego liczy się okno do NASTĘPNEGO wpisu, nie od najnowszego. */
    grupa.ostatni = Number.isFinite(czas) ? czas : grupa.ostatni;
    dodajDoZebranych(grupa.zebrane, w);
  }

  return gotowe.map((g) => ({
    seq: g.seq,
    at: g.at,
    wezel: g.wezel,
    wpisow: g.wpisow,
    czesci: czesciGrupy(g.zebrane, slowa),
  }));
}
